import React, { useEffect, useRef, useState } from "react";

const StickyTableContainer = ({ children, className = "" }) => {
  const tableScrollRef = useRef(null);
  const fixedScrollRef = useRef(null);
  const fixedHeaderRef = useRef(null);
  const syncingRef = useRef(false);
  const [bar, setBar] = useState({ visible: false, left: 0, width: 0, contentWidth: 0 });

  useEffect(() => {
    const tableScroll = tableScrollRef.current;
    if (!tableScroll) return undefined;

    const updateBar = () => {
      const rect = tableScroll.getBoundingClientRect();
      const contentWidth = tableScroll.scrollWidth;
      const hasHorizontalOverflow = contentWidth > tableScroll.clientWidth + 1;
      const tableContinuesBelowViewport = rect.top < window.innerHeight && rect.bottom > window.innerHeight;

      setBar({
        visible: hasHorizontalOverflow && tableContinuesBelowViewport,
        left: Math.max(0, rect.left),
        width: Math.min(rect.right, window.innerWidth) - Math.max(0, rect.left),
        contentWidth,
      });

      const table = tableScroll.querySelector("table");
      const originalHead = table?.querySelector("thead");
      const fixedHeader = fixedHeaderRef.current;
      if (!table || !originalHead || !fixedHeader) return;

      const headerOffset = window.innerWidth >= 1024 ? 80 : 64;
      const headRect = originalHead.getBoundingClientRect();
      const showHeader = headRect.top < headerOffset && rect.bottom > headerOffset + headRect.height;
      fixedHeader.style.display = showHeader ? "block" : "none";
      fixedHeader.style.top = `${headerOffset}px`;
      fixedHeader.style.left = `${Math.max(0, rect.left)}px`;
      fixedHeader.style.width = `${Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(0, rect.left))}px`;

      if (showHeader) {
        const clonedTable = table.cloneNode(false);
        const clonedHead = originalHead.cloneNode(true);
        clonedTable.className = `${table.className} sticky-table-fixed-clone`;
        clonedTable.style.width = `${table.scrollWidth}px`;
        clonedTable.style.minWidth = `${table.scrollWidth}px`;
        clonedTable.style.transform = `translateX(-${tableScroll.scrollLeft}px)`;
        clonedTable.appendChild(clonedHead);

        const originalCells = originalHead.querySelectorAll("th");
        const clonedCells = clonedHead.querySelectorAll("th");
        originalCells.forEach((cell, index) => {
          const width = cell.getBoundingClientRect().width;
          clonedCells[index].style.width = `${width}px`;
          clonedCells[index].style.minWidth = `${width}px`;
          clonedCells[index].style.maxWidth = `${width}px`;
        });
        fixedHeader.replaceChildren(clonedTable);
      }
    };

    const syncFromTable = () => {
      if (syncingRef.current || !fixedScrollRef.current) return;
      syncingRef.current = true;
      fixedScrollRef.current.scrollLeft = tableScroll.scrollLeft;
      const fixedTable = fixedHeaderRef.current?.querySelector("table");
      if (fixedTable) fixedTable.style.transform = `translateX(-${tableScroll.scrollLeft}px)`;
      syncingRef.current = false;
    };
    const syncFromFixedBar = () => {
      if (syncingRef.current || !fixedScrollRef.current) return;
      syncingRef.current = true;
      tableScroll.scrollLeft = fixedScrollRef.current.scrollLeft;
      syncingRef.current = false;
    };

    updateBar();
    window.addEventListener("scroll", updateBar, { passive: true });
    window.addEventListener("resize", updateBar);
    tableScroll.addEventListener("scroll", syncFromTable, { passive: true });
    const resizeObserver = new ResizeObserver(updateBar);
    resizeObserver.observe(tableScroll);
    if (tableScroll.firstElementChild) resizeObserver.observe(tableScroll.firstElementChild);

    const fixedScroll = fixedScrollRef.current;
    fixedScroll?.addEventListener("scroll", syncFromFixedBar, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateBar);
      window.removeEventListener("resize", updateBar);
      tableScroll.removeEventListener("scroll", syncFromTable);
      fixedScroll?.removeEventListener("scroll", syncFromFixedBar);
      resizeObserver.disconnect();
    };
  }, []);

  return <>
    <div ref={tableScrollRef} className={`overflow-x-auto ${className}`}>{children}</div>
    <div
      ref={fixedHeaderRef}
      aria-hidden="true"
      className="fixed z-40 hidden overflow-hidden bg-gray-100 shadow-md"
    />
    <div
      ref={fixedScrollRef}
      aria-label="Défilement horizontal du tableau"
      className={`fixed bottom-0 z-40 h-5 overflow-x-auto overflow-y-hidden border-t border-slate-300 bg-white shadow-[0_-2px_8px_rgba(15,23,42,0.18)] ${bar.visible ? "block" : "hidden"}`}
      style={{ left: bar.left, width: bar.width }}
    >
      <div style={{ width: bar.contentWidth, height: 1 }} />
    </div>
  </>;
};

export default StickyTableContainer;
